import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Toaster, toast } from 'sonner';
import { Store, Plus, Edit2, Trash2, LogOut, Heart, Eye, TrendingUp, Clock, CheckCircle2, XCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import UMKMForm, { UMKM_EMPTY } from '../components/admin/UMKMForm';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const cfg = { withCredentials: true };

const STATUS = {
  pending: { label: 'Menunggu Approval', cls: 'bg-amber-50 text-amber-700', Icon: Clock },
  approved: { label: 'Tayang', cls: 'bg-emerald-50 text-emerald-700', Icon: CheckCircle2 },
  rejected: { label: 'Ditolak', cls: 'bg-red-50 text-red-700', Icon: XCircle },
};

const MerchantDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [ads, setAds] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(UMKM_EMPTY);

  const load = () => axios.get(`${API_URL}/api/umkm/mine`, cfg).then((r) => setAds(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(UMKM_EMPTY); setOpen(true); };
  const openEdit = (a) => {
    setEditing(a);
    setForm({ name: a.name, description: a.description, category: a.category, location: a.location || '', whatsapp: a.whatsapp, video_url: a.video_url || '', images: a.images || [], logo_url: a.logo_url || '' });
    setOpen(true);
  };
  const submit = async (payload) => {
    try {
      if (editing) { await axios.put(`${API_URL}/api/umkm/${editing.id}`, payload, cfg); toast.success('Iklan diperbarui — menunggu approval ulang'); }
      else { await axios.post(`${API_URL}/api/umkm`, payload, cfg); toast.success('Iklan dikirim — menunggu approval Takmir'); }
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Gagal menyimpan'); }
  };
  const del = async (id) => {
    if (!window.confirm('Hapus iklan ini?')) return;
    try { await axios.delete(`${API_URL}/api/umkm/${id}`, cfg); toast.success('Iklan dihapus'); load(); }
    catch (e) { toast.error('Gagal menghapus'); }
  };
  const bump = async (id) => {
    try { const { data } = await axios.post(`${API_URL}/api/umkm/${id}/bump`, {}, cfg); toast.success(`Iklan di-sundul ke atas! Sisa hari ini: ${data.remaining}x`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Gagal menyundul'); }
  };
  const handleLogout = async () => { await logout(); navigate('/login'); };

  return (
    <div className="min-h-screen bg-stone-50" data-testid="merchant-dashboard">
      <Toaster position="top-right" richColors />
      <div className="bg-[#022C22] text-white sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-400/90 flex items-center justify-center"><Store className="w-6 h-6 text-emerald-950" /></div>
            <div><h1 className="text-xl font-heading font-bold">Dashboard Merchant</h1><p className="text-xs text-white/70">{user?.name}</p></div>
          </div>
          <button data-testid="merchant-logout-btn" onClick={handleLogout} className="p-2 rounded-full hover:bg-white/10"><LogOut className="w-5 h-5" /></button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-heading font-bold text-primary-dark">Iklan Saya</h2>
            <p className="text-sm font-body text-text-secondary">Kelola & sundul iklan usaha Anda (maks 2x/hari)</p>
          </div>
          <button data-testid="merchant-add-btn" onClick={openNew} className="flex items-center gap-2 px-4 py-2 bg-emerald-700 hover:bg-emerald-800 text-white rounded-full font-medium"><Plus className="w-5 h-5" /> Pasang Iklan</button>
        </div>

        {ads.length === 0 ? (
          <div className="bg-white rounded-2xl border border-stone-200 p-12 text-center">
            <Store className="w-16 h-16 text-stone-300 mx-auto mb-4" />
            <p className="text-text-secondary font-body mb-4">Belum ada iklan. Pasang iklan pertama Anda!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {ads.map((a) => {
              const st = STATUS[a.status] || STATUS.pending; const SIcon = st.Icon;
              return (
                <motion.div key={a.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl border border-stone-200 p-4" data-testid={`merchant-ad-${a.id}`}>
                  <div className="flex gap-3">
                    <div className="w-20 h-20 rounded-xl bg-cover bg-center bg-emerald-50 flex-shrink-0" style={{ backgroundImage: a.logo_url ? `url('${a.logo_url}')` : 'none' }}>
                      {!a.logo_url && <div className="w-full h-full flex items-center justify-center"><Store className="w-8 h-8 text-emerald-300" /></div>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-body font-semibold text-text-primary truncate">{a.name}</h3>
                      <span className="inline-block px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 text-[11px] font-medium my-1">{a.category}</span>
                      <span className={`flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full font-medium w-fit ${st.cls}`}><SIcon className="w-3 h-3" /> {st.label}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 mt-3 text-xs font-body text-stone-500">
                    <span className="flex items-center gap-1"><Heart className="w-3.5 h-3.5 text-rose-500" /> {a.likes || 0}</span>
                    <span className="flex items-center gap-1"><Eye className="w-3.5 h-3.5" /> {a.view_count || 0}</span>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <button data-testid={`merchant-bump-${a.id}`} onClick={() => bump(a.id)} disabled={a.status !== 'approved'} className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-amber-400 hover:bg-amber-300 disabled:bg-stone-100 disabled:text-stone-400 text-emerald-950 rounded-xl text-sm font-medium"><TrendingUp className="w-4 h-4" /> Sundul</button>
                    <button data-testid={`merchant-edit-${a.id}`} onClick={() => openEdit(a)} className="px-3 py-2 bg-stone-100 hover:bg-stone-200 text-stone-700 rounded-xl"><Edit2 className="w-4 h-4" /></button>
                    <button data-testid={`merchant-delete-${a.id}`} onClick={() => del(a.id)} className="px-3 py-2 bg-red-50 hover:bg-red-100 text-red-700 rounded-xl"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
      <UMKMForm open={open} formData={form} setFormData={setForm} onSubmit={submit} onClose={() => setOpen(false)} editing={editing} />
    </div>
  );
};

export default MerchantDashboard;
